import {
  ListItemText,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import React from "react";

import { StyledContentList, StyledContentListItem } from "src/components";
import { ariaLabel } from "src/constants";

const StyledTable = styled(Table)`
  min-width: 700px;
  & .MuiTableCell-root {
    padding: 10px;
  }
`;

const CategoryTableCell = styled(TableCell)`
  background-color: ${(props) => props.theme.palette.gray.background};
  font-weight: 800;
`;

const DescriptionTableCell = styled(TableCell)`
  max-width: 500px;
`;

const ActionRowTableCell = styled(TableCell)`
  border-left: 1px solid ${(props) => props.theme.palette.gray.border};
`;

const ACTION_STATUS = {
  ComingSoon: "À venir",
  Completed: "Réalisé",
  InProgress: "En cours",
  ContinuousImprovement: "Amélioration continue",
  NotApplicable: "N/C",
} as const;

type ActionStatus = typeof ACTION_STATUS[keyof typeof ACTION_STATUS];

type YearActionStatus = {
  2025: ActionStatus;
  2026: ActionStatus;
  2027: ActionStatus;
};

type ActionRow = {
  description: React.ReactNode;
  yearStatus: YearActionStatus;
};

type ActionCategory = {
  title: string;
  actions: Array<ActionRow>;
};

const renderHeaderRow = (title: string) => (
  <TableRow>
    <CategoryTableCell aria-label={title}>{title}</CategoryTableCell>
    <CategoryTableCell sx={{ borderLeft: "1px solid #e0e0e0" }} align="left">
      2025
    </CategoryTableCell>
    <CategoryTableCell sx={{ borderLeft: "1px solid #e0e0e0" }} align="left">
      2026
    </CategoryTableCell>
    <CategoryTableCell sx={{ borderLeft: "1px solid #e0e0e0" }} align="left">
      2027
    </CategoryTableCell>
  </TableRow>
);

const renderCategoryRow = (title: string) => (
  <TableRow aria-label={title}>
    <CategoryTableCell colSpan={4} aria-label={title}>
      {title}
    </CategoryTableCell>
  </TableRow>
);

const renderActionRow = (action: ActionRow, index: number) => {
  const statuses = Object.values(action.yearStatus);
  const allSameStatus = statuses.every((status) => status === statuses[0]);

  return (
    <TableRow key={index}>
      <DescriptionTableCell align="left">{action.description}</DescriptionTableCell>
      {allSameStatus ? (
        <ActionRowTableCell colSpan={3} align="center">
          {statuses[0]}
        </ActionRowTableCell>
      ) : (
        Object.entries(action.yearStatus).map(([, status], sIndex) => (
          <ActionRowTableCell key={sIndex} align="center">
            {status}
          </ActionRowTableCell>
        ))
      )}
    </TableRow>
  );
};

const ActionPlanTable = () => {
  const actionPlans: Array<ActionCategory> = [
    {
      title: "Évaluation et suivi de l'accessibilité",
      actions: [
        {
          description: "Audit de l'accessibilité numérique de nos espaces clients",
          yearStatus: {
            2025: ACTION_STATUS.Completed,
            2026: ACTION_STATUS.ComingSoon,
            2027: ACTION_STATUS.ComingSoon,
          },
        },
        {
          description: "Publier le taux de conformité",
          yearStatus: {
            2025: ACTION_STATUS.Completed,
            2026: ACTION_STATUS.NotApplicable,
            2027: ACTION_STATUS.NotApplicable,
          },
        },
        {
          description: "Actualiser le taux de conformité",
          yearStatus: {
            2025: ACTION_STATUS.NotApplicable,
            2026: ACTION_STATUS.ComingSoon,
            2027: ACTION_STATUS.ComingSoon,
          },
        },
        {
          description: "Étudier les retours des utilisateurs sur l'accessibilité du site",
          yearStatus: {
            2025: ACTION_STATUS.InProgress,
            2026: ACTION_STATUS.ComingSoon,
            2027: ACTION_STATUS.ComingSoon,
          },
        },
      ],
    },
    {
      title: "Documentation et conformité administrative",
      actions: [
        {
          description:
            "Publier la(les) page(s) officielle(s) (i.e. la déclaration d'accessibilité) ",
          yearStatus: {
            2025: ACTION_STATUS.Completed,
            2026: ACTION_STATUS.ComingSoon,
            2027: ACTION_STATUS.ComingSoon,
          },
        },
        {
          description: "Élaboration et publication du plan pluriannuel de mise en conformité ",
          yearStatus: {
            2025: ACTION_STATUS.Completed,
            2026: ACTION_STATUS.ComingSoon,
            2027: ACTION_STATUS.ComingSoon,
          },
        },
      ],
    },
    {
      title: "Amélioration progressive de l'accessibilité ",
      actions: [
        {
          description: (
            <>
              Évaluer les non-conformités identifiées et envisager des actions d'amélioration. Les
              points d'amélioration incluent, entre autres:
              <StyledContentList disablePadding>
                <StyledContentListItem disablePadding>
                  <ListItemText>Révision de la charte graphique </ListItemText>
                </StyledContentListItem>
                <StyledContentListItem disablePadding>
                  <ListItemText>Révision du code de source du site </ListItemText>
                </StyledContentListItem>
                <StyledContentListItem disablePadding>
                  <ListItemText>Contrôle de saisie des formulaires </ListItemText>
                </StyledContentListItem>
              </StyledContentList>
            </>
          ),
          yearStatus: {
            2025: ACTION_STATUS.Completed,
            2026: ACTION_STATUS.ComingSoon,
            2027: ACTION_STATUS.ComingSoon,
          },
        },
        {
          description: "Mise à niveau de l'espace pour respect des critères d'accessibilité  ",
          yearStatus: {
            2025: ACTION_STATUS.Completed,
            2026: ACTION_STATUS.ComingSoon,
            2027: ACTION_STATUS.ComingSoon,
          },
        },
      ],
    },
    {
      title: "Sensibilisation et formation des équipes ",
      actions: [
        {
          description:
            "Création de la documentation au sujet de l'accessibilité numérique chez Emeria Technologies ",
          yearStatus: {
            2025: ACTION_STATUS.Completed,
            2026: ACTION_STATUS.InProgress,
            2027: ACTION_STATUS.InProgress,
          },
        },
        {
          description:
            "Sensibilisation des équipes Emeria Technologies au sujet de l'accessibilité numérique ",
          yearStatus: {
            2025: ACTION_STATUS.Completed,
            2026: ACTION_STATUS.ComingSoon,
            2027: ACTION_STATUS.ComingSoon,
          },
        },
        {
          description: "Formation des nouveaux collaborateurs aux sujets d'accessibilité ",
          yearStatus: {
            2025: ACTION_STATUS.InProgress,
            2026: ACTION_STATUS.ComingSoon,
            2027: ACTION_STATUS.ComingSoon,
          },
        },
      ],
    },
    {
      title: "Benchmark et veille concurrentielle ",
      actions: [
        {
          description: "Benchmark d'autres sites web pour déterminer les best practices ",
          yearStatus: {
            2025: ACTION_STATUS.ContinuousImprovement,
            2026: ACTION_STATUS.ContinuousImprovement,
            2027: ACTION_STATUS.ContinuousImprovement,
          },
        },
        {
          description: "Maintenir une veille sur l'évolution des bonnes pratiques en accessibilité",
          yearStatus: {
            2025: ACTION_STATUS.ContinuousImprovement,
            2026: ACTION_STATUS.ContinuousImprovement,
            2027: ACTION_STATUS.ContinuousImprovement,
          },
        },
      ],
    },
    {
      title: "Tests et outils d'amélioration ",
      actions: [
        {
          description:
            "Mise en œuvre de tests automatisés pour identifier des pistes d'amélioration  ",
          yearStatus: {
            2025: ACTION_STATUS.Completed,
            2026: ACTION_STATUS.ComingSoon,
            2027: ACTION_STATUS.ComingSoon,
          },
        },
      ],
    },
    {
      title: "Gouvernance et organisation ",
      actions: [
        {
          description:
            "Organisation interne : Mise en place d'une équipe dédiée aux sujets d'accessibilité",
          yearStatus: {
            2025: ACTION_STATUS.Completed,
            2026: ACTION_STATUS.NotApplicable,
            2027: ACTION_STATUS.NotApplicable,
          },
        },
      ],
    },
  ];

  return (
    <TableContainer sx={{ marginTop: 2 }} component={Paper}>
      <StyledTable aria-label={ariaLabel.actionPlanTableLabel}>
        <TableBody>
          {actionPlans.map((plan, index) => (
            <React.Fragment key={index}>
              {index === 0 ? renderHeaderRow(plan.title) : renderCategoryRow(plan.title)}
              {plan.actions.map((action, aIndex) => renderActionRow(action, aIndex))}
            </React.Fragment>
          ))}
        </TableBody>
      </StyledTable>
    </TableContainer>
  );
};

export default ActionPlanTable;
